import mapboxgl from 'mapbox-gl'
import { RouteRenderer } from './routeRenderer'
import { FlickerFreeRouteRenderer } from './flickerFreeRouteRenderer'

/**
 * UnifiedRouteRenderer - A clean interface for route rendering that handles
 * both regular and embed modes consistently
 */
export class UnifiedRouteRenderer {
  private map: mapboxgl.Map
  private renderer: RouteRenderer | FlickerFreeRouteRenderer
  private isEmbedMode: boolean
  private alwaysRenderRoutes: boolean
  private selectedRouteId: string | null = null
  private hoveredRouteId: string | null = null
  private renderingRouteId: string | null = null
  private styleChangeHandler: (() => void) | null = null

  constructor(map: mapboxgl.Map, embedMode: boolean = false, alwaysRenderRoutes: boolean = false) {
    this.map = map
    this.isEmbedMode = embedMode
    this.alwaysRenderRoutes = alwaysRenderRoutes
    
    // Use FlickerFreeRenderer for regular mode, RouteRenderer for embed mode
    if (embedMode) {
      this.renderer = new RouteRenderer(map)
      // RouteRenderer doesn't handle style changes, so we need to do it here
      this.setupStyleChangeHandler()
    } else {
      this.renderer = new FlickerFreeRouteRenderer(map)
      // FlickerFreeRenderer handles its own style changes
    }
  }

  /**
   * Render a route on hover
   */
  renderHoveredRoute(route: any): void {
    console.log('🖱️ UnifiedRouteRenderer.renderHoveredRoute called')
    console.log('  - isEmbedMode:', this.isEmbedMode)
    console.log('  - alwaysRenderRoutes:', this.alwaysRenderRoutes)
    console.log('  - route:', route?.name || route?.id)
    
    const routeId = route.id || route.firebaseRoute?.id
    if (!routeId) return

    this.hoveredRouteId = routeId
    this.renderingRouteId = routeId
    
    // In embed mode with always render, highlight the route instead of rendering
    if (this.isEmbedMode && this.alwaysRenderRoutes) {
      console.log('  - highlighting embed route on hover')
      this.highlightEmbedRoute(routeId)
      this.renderingRouteId = null
      return
    }
    
    // Regular rendering for non-embed mode or when routes aren't always visible
    if (this.renderer instanceof FlickerFreeRouteRenderer) {
      this.renderer.renderRoute(route, 'hover').then(() => {
        this.renderingRouteId = null
      })
    } else {
      this.renderer.renderRoute(route, false)
      // RouteRenderer doesn't return a promise, so clear immediately
      setTimeout(() => {
        this.renderingRouteId = null
      }, 200)
    }
  }

  /**
   * Clear the hovered route
   */
  clearHoveredRoute(): void {
    // Don't clear if this route is currently selected (popup is open)
    if (this.hoveredRouteId && this.hoveredRouteId === this.selectedRouteId) {
      console.log('🛡️ Not clearing hovered route - it is currently selected')
      return
    }
    
    // Don't clear if this route is currently being rendered
    if (this.hoveredRouteId && this.hoveredRouteId === this.renderingRouteId) {
      console.log('🛡️ Not clearing hovered route - it is currently being rendered')
      return
    }
    
    const previousHoveredId = this.hoveredRouteId
    this.hoveredRouteId = null
    
    // In embed mode with always render, unhighlight the route
    if (this.isEmbedMode && this.alwaysRenderRoutes && previousHoveredId) {
      console.log('  - unhighlighting embed route on hover clear')
      this.unhighlightEmbedRoute(previousHoveredId)
      return
    }
    
    // Regular clearing for non-embed mode
    if (this.renderer instanceof FlickerFreeRouteRenderer) {
      this.renderer.clearHoveredRoute()
    } else if (!this.selectedRouteId) {
      // Only clear if no route is selected
      this.renderer.clearRenderedRoute()
    }
  }

  /**
   * Render a selected route (from popup)
   */
  renderSelectedRoute(route: any): void {
    const routeId = route.id || route.firebaseRoute?.id
    if (!routeId) return

    console.log('UnifiedRouteRenderer.renderSelectedRoute:', routeId, route)
    
    // Clear any previous selection
    if (this.selectedRouteId && this.selectedRouteId !== routeId) {
      this.clearSelectedRoute()
    }
    
    this.selectedRouteId = routeId
    
    if (this.isEmbedMode && this.alwaysRenderRoutes) {
      // In embed mode with always render, just highlight the route
      this.highlightEmbedRoute(routeId)
    } else {
      // In regular mode or embed without always render, render the route
      // Use the firebaseRoute if available, otherwise use the route itself
      const routeToRender = route.firebaseRoute || route
      
      if (this.isEmbedMode) {
        // In embed mode (markers only), render and keep the route visible
        (this.renderer as RouteRenderer).renderRoute(routeToRender, true)
      } else if (this.renderer instanceof FlickerFreeRouteRenderer) {
        // Force re-render even if already selected
        this.renderer.clearSelectedRoute()
        this.renderer.renderRoute(routeToRender, 'selected')
      } else {
        (this.renderer as RouteRenderer).renderRoute(routeToRender, true)
      }
    }
  }

  /**
   * Clear the selected route
   */
  clearSelectedRoute(): void {
    const previousSelectedId = this.selectedRouteId
    this.selectedRouteId = null
    
    if (this.isEmbedMode && this.alwaysRenderRoutes && previousSelectedId) {
      // In embed mode with always render, unhighlight the route
      this.unhighlightEmbedRoute(previousSelectedId)
    } else {
      // In regular mode or embed without always render, clear the route
      if (this.renderer instanceof FlickerFreeRouteRenderer) {
        this.renderer.clearSelectedRoute()
      } else if (!this.hoveredRouteId) {
        // Only clear if no route is hovered
        this.renderer.clearRenderedRoute()
      }
    }
  }

  /**
   * Highlight a route in embed mode
   */
  private highlightEmbedRoute(routeId: string): void {
    // In embed mode, routes are rendered with static layers
    // We need to find the right layer pattern
    const style = this.map.getStyle()
    if (!style || !style.layers) return

    console.log('Highlighting embed route:', routeId)

    // The embed layers use the pattern: embed-static-routes-layer
    const mainLayerId = 'embed-static-routes-layer'
    const strokeLayerId = 'embed-static-routes-stroke'
    
    try {
      // Get the current data
      const source = this.map.getSource('embed-static-routes') as mapboxgl.GeoJSONSource
      if (!source) return
      
      // Update paint properties to highlight the selected route
      // Use a data-driven style to highlight routes - handle both single and multi-segment routes
      // We check if the routeId matches exactly OR starts with routeId followed by "-segment-"
      const routeFilter = [
        'any',
        ['==', ['get', 'routeId'], routeId],
        ['==', ['slice', ['get', 'routeId'], 0, routeId.length + 9], `${routeId}-segment-`]
      ]
      
      this.map.setPaintProperty(mainLayerId, 'line-width', [
        'case',
        routeFilter,
        5, // Selected width
        3  // Default width
      ])
      
      this.map.setPaintProperty(mainLayerId, 'line-opacity', [
        'case',
        routeFilter,
        1,   // Selected opacity
        0.6  // Default opacity (dimmed)
      ])
      
      // Also update stroke layer
      this.map.setPaintProperty(strokeLayerId, 'line-width', [
        'case',
        routeFilter,
        7, // Selected stroke width
        5  // Default stroke width
      ])
    } catch (e) {
      console.warn('Error highlighting route:', e)
    }
  }

  /**
   * Unhighlight a route in embed mode
   */
  private unhighlightEmbedRoute(routeId: string): void {
    console.log('Unhighlighting embed route:', routeId)
    
    const mainLayerId = 'embed-static-routes-layer'
    const strokeLayerId = 'embed-static-routes-stroke'
    
    try {
      // Reset all routes to default styling
      this.map.setPaintProperty(mainLayerId, 'line-width', 3)
      this.map.setPaintProperty(mainLayerId, 'line-opacity', 0.8)
      this.map.setPaintProperty(strokeLayerId, 'line-width', 5)
    } catch (e) {
      console.warn('Error unhighlighting route:', e)
    }
  }

  /**
   * Set up style change handler for embed mode
   */
  private setupStyleChangeHandler(): void {
    this.styleChangeHandler = () => {
      console.log('🎨 UnifiedRouteRenderer: Map style changed in embed mode')
      // In embed mode with always render routes, we need to re-render static routes
      // This will be handled by the parent component (RouteMarkers)
      // For now, just clear the renderer state
      if (this.renderer instanceof RouteRenderer) {
        this.renderer.clearRenderedRoute()
      }
    }
    this.map.on('style.load', this.styleChangeHandler)
  }

  /**
   * Check if a route is currently being rendered
   */
  isRouteRendering(routeId: string): boolean {
    return this.renderingRouteId === routeId
  }

  /**
   * Update the alwaysRenderRoutes flag
   */
  setAlwaysRenderRoutes(alwaysRenderRoutes: boolean): void {
    this.alwaysRenderRoutes = alwaysRenderRoutes
  }

  /**
   * Clean up and destroy the renderer
   */
  destroy(): void {
    // Remove style change listener if it exists
    if (this.styleChangeHandler && this.map) {
      this.map.off('style.load', this.styleChangeHandler)
      this.styleChangeHandler = null
    }
    
    this.renderer.destroy()
    this.selectedRouteId = null
    this.hoveredRouteId = null
    this.renderingRouteId = null
  }
}